import { useState } from "react";
import type { AdminToolCall } from "../../hooks/useAdminToolCalls";
import {
  parseSubTools,
  prettyArgs,
  subToolBreakdown,
  toolCallStatusDecision,
  type ToolCallStatusLabel,
} from "../../lib/toolCallStatus";
import { formatBytes, formatDuration } from "../../utils/formatToolCall";
import { formatDateTime, formatDateTimeCompact } from "../../utils/formatDateTime";
import styles from "./ToolCallsTable.module.css";

// Maps the pure status label to the pill className. The label decision lives in
// lib/toolCallStatus so it stays testable; only the styling coupling is here.
const STATUS_CLASS: Record<ToolCallStatusLabel, string> = {
  ok: styles.statusOk ?? "",
  error: styles.statusError ?? "",
  mixed: styles.statusPartial ?? "",
  skipped: styles.statusUnconnected ?? "",
};

export type ToolCallsTablePager = {
  readonly page: number;
  readonly canPrev: boolean;
  readonly canNext: boolean;
  readonly pageStart: number;
  readonly pageEnd: number;
  readonly onPrev: () => void;
  readonly onNext: () => void;
};

export type ToolCallsTableProps = {
  readonly calls: ReadonlyArray<AdminToolCall>;
  readonly isFetching: boolean;
  // Toggles the Customer column (default true). The per-instance dashboard
  // hides it since every row shares the same tenant.
  readonly showCustomer?: boolean | undefined;
  readonly pager?: ToolCallsTablePager | undefined;
};

export const ToolCallsTable = ({
  calls,
  isFetching,
  showCustomer = true,
  pager,
}: ToolCallsTableProps): JSX.Element => {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const colSpan = showCustomer ? 7 : 6;

  const toggleRow = (id: number): void => {
    setExpandedId((current) => (current === id ? null : id));
  };

  return (
    <>
      <table
        className={isFetching ? `${styles.table} ${styles.tableFetching}` : styles.table}
      >
        <thead>
          <tr>
            <th>When</th>
            {showCustomer && <th>Customer</th>}
            <th>Tool</th>
            <th>Integration</th>
            <th className={styles.statusCell}>Status</th>
            <th>Sub-tools</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          {calls.map((row: AdminToolCall) => (
            <Row
              key={row.id}
              row={row}
              showCustomer={showCustomer}
              colSpan={colSpan}
              isExpanded={expandedId === row.id}
              onToggle={() => toggleRow(row.id)}
            />
          ))}
        </tbody>
      </table>
      {pager != null && (pager.canPrev || pager.canNext) && (
        <div className={styles.pager}>
          <button
            type="button"
            className={styles.pagerBtn}
            onClick={() => pager.onPrev()}
            disabled={!pager.canPrev}
          >
            ‹ Prev
          </button>
          <span className={styles.pagerStatus}>
            Page {pager.page} · {pager.pageStart}–{pager.pageEnd}
          </span>
          <button
            type="button"
            className={styles.pagerBtn}
            onClick={() => pager.onNext()}
            disabled={!pager.canNext}
          >
            Next ›
          </button>
        </div>
      )}
    </>
  );
};

type RowProps = {
  row: AdminToolCall;
  showCustomer: boolean;
  colSpan: number;
  isExpanded: boolean;
  onToggle: () => void;
};

const Row = ({ row, showCustomer, colSpan, isExpanded, onToggle }: RowProps): JSX.Element => {
  const statusLabel = toolCallStatusDecision(row);
  return (
    <>
      <tr className={styles.row} onClick={onToggle}>
        <td className={`${styles.muted} ${styles.when}`} title={formatDateTime(row.startedAt)}>
          {formatDateTimeCompact(row.startedAt)}
        </td>
        {showCustomer && (
          <td className={styles.email}>
            <span className={styles.emailText} title={row.ownerEmail ?? row.tenantId}>
              {row.ownerEmail ?? row.tenantId}
            </span>
          </td>
        )}
        <td className={styles.toolName} title={row.toolName}>
          <span className={styles.toolNameText}>{row.toolName}</span>
        </td>
        <td className={`${styles.muted} ${styles.when}`}>{row.integrationSlug}</td>
        <td className={styles.statusCell}>
          <span className={`${styles.statusPill} ${STATUS_CLASS[statusLabel]}`}>{statusLabel}</span>
          {row.errorKind && <span className={styles.errorKind}>{row.errorKind}</span>}
        </td>
        <td>
          <SubToolCounts row={row} />
        </td>
        <td className={styles.muted}>{formatDuration(row.durationMs)}</td>
      </tr>
      {isExpanded && (
        <tr className={styles.detailRow}>
          <td colSpan={colSpan}>
            <div className={styles.detailWrap}>
              <div className={styles.detailMeta}>
                <span>
                  <strong>Started:</strong> {formatDateTime(row.startedAt)}
                </span>
                <span>
                  <strong>Tenant:</strong> {row.tenantId}
                </span>
                <span>
                  <strong>Provider:</strong> {row.provider}
                </span>
                <span>
                  <strong>Request bytes:</strong> {formatBytes(row.requestBytes)}
                </span>
                <span>
                  <strong>Response bytes:</strong> {formatBytes(row.responseBytes)}
                </span>
                {row.requestId && (
                  <span>
                    <strong>Request id:</strong> {row.requestId}
                  </span>
                )}
                {row.truncated && (
                  <span className={styles.truncatedBadge}>truncated</span>
                )}
              </div>
              {row.subToolCount != null && (
                <div className={styles.subToolsSection}>
                  <span className={styles.subToolsHeading}>
                    Sub-tools ({row.subToolSuccessCount ?? 0} of {row.subToolCount} succeeded)
                  </span>
                  <div className={styles.chipList}>
                    {parseSubTools(row.subTools).map((entry, i) => {
                      const chipClass = entry.unconnected
                        ? styles.subToolUnconnected
                        : entry.ok
                          ? styles.subToolOk
                          : styles.subToolFailed;
                      const chipTitle = entry.unconnected
                        ? "not connected (rejected locally, not billed)"
                        : entry.ok
                          ? "succeeded"
                          : "failed";
                      return (
                        <span
                          key={`${entry.slug}-${i}`}
                          className={`${styles.subToolChip} ${chipClass}`}
                          title={chipTitle}
                        >
                          {entry.slug}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              <pre className={styles.argsBlock}>{prettyArgs(row.requestArgs)}</pre>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// Inline tri-column count for the Sub-tools cell: total, OK, fail. Shown only
// for COMPOSIO_MULTI_EXECUTE_TOOL rows (where subToolCount is non-null); other
// rows get a muted dash so the column doesn't visually disappear. The failure
// count uses muted styling when zero so partial failures jump out in red.
const SubToolCounts = ({ row }: { row: AdminToolCall }): JSX.Element => {
  const b = subToolBreakdown(row);
  if (b == null) {
    return <span className={styles.muted}>—</span>;
  }
  return (
    <span className={styles.subToolCounts}>
      <span className={styles.subToolTotal}>{b.count}</span>
      <span className={styles.subToolDot}>·</span>
      <span className={styles.subToolOkText}>{b.success} ok</span>
      <span className={styles.subToolDot}>·</span>
      <span className={b.failed > 0 ? styles.subToolFailText : styles.subToolFailZero}>{b.failed} fail</span>
      {b.unconnected > 0 && (
        <>
          <span className={styles.subToolDot}>·</span>
          <span className={styles.subToolUnconnectedText}>{b.unconnected} skipped</span>
        </>
      )}
    </span>
  );
};
